import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { toast } from "@redotech/redo-web/alert";
import { memo, useMemo, useRef, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { MergeModal } from "./merge-modal";

export const MergeSuggestionModal = memo(function MergeSuggestionModal({
  suggestions,
  open,
  setOpen,
  triggerReload,
  rejectedSuggestionIds,
  setRejectedSuggestionIds,
}: {
  suggestions: Record<string, FulfillmentGroupData[]> | undefined;
  open: boolean;
  setOpen: (val: boolean) => void;
  triggerReload: () => void;
  rejectedSuggestionIds: string[];
  setRejectedSuggestionIds: (ids: string[]) => void;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
  const [suggestedIndex, setSuggestedIndex] = useState<number>(0);
  const idsPendingRejectionRef = useRef<string[]>([]);

  const totalSuggestions = useMemo(
    () => Object.keys(suggestions ?? {}).length,
    [suggestions],
  );

  const updateIdsPendingRejection = (newIds: string[]) => {
    idsPendingRejectionRef.current = newIds;
  };

  const [_, rejectSuggestion] = useTriggerLoad(async (signal) => {
    setRejectedSuggestionIds([
      ...rejectedSuggestionIds,
      ...idsPendingRejectionRef.current,
    ]);

    const rejectedGroups = Object.entries(suggestions ?? {})
      .filter(([suggestionId]) =>
        idsPendingRejectionRef.current.includes(suggestionId),
      )
      .flatMap(([, groups]) => groups);

    if (!rpcClient || !rejectedGroups.length) {
      return;
    }

    try {
      await rpcClient.rejectMergeSuggestion(
        { groupIds: rejectedGroups.map((group) => group._id) },
        { signal },
      );
    } catch (e) {
      toast("Unable to dismiss merge suggestion", { variant: "error" });
      console.error((e as Error).message);
      setRejectedSuggestionIds(
        rejectedSuggestionIds.filter(
          (id) => !idsPendingRejectionRef.current.includes(id),
        ),
      );
    } finally {
      updateIdsPendingRejection([]);
    }
  });

  const onClose = useHandler(() => {
    setSuggestedIndex(0);
    if (needsRefresh) {
      triggerReload();
      setNeedsRefresh(false);
    }
    if (idsPendingRejectionRef.current.length > 0) {
      rejectSuggestion();
    }
    setOpen(false);
  });

  const groups = useMemo(() => {
    return Object.values(suggestions ?? {})?.[suggestedIndex] ?? [];
  }, [suggestedIndex, suggestions]);

  const onNext = useHandler(() => {
    if (suggestedIndex < totalSuggestions - 1) {
      setSuggestedIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  });

  const onSkip = useHandler(() => {
    onNext();
  });

  return (
    <MergeModal
      analyticsSource="suggestion"
      groups={groups}
      headerTitle={`Merge shipments (${suggestedIndex + 1} of ${Object.values(suggestions ?? {}).length})`}
      onCancel={() => {
        const id = Object.keys(suggestions ?? {})?.[suggestedIndex];
        if (id) {
          updateIdsPendingRejection([...idsPendingRejectionRef.current, id]);
        }
        onNext();
      }}
      onModalClose={onClose}
      onSkip={onSkip}
      open={open}
      postMerge={async (group: FulfillmentGroupData) => {
        setNeedsRefresh(true);
        onNext();
      }}
    />
  );
});
